/**
 * MapFilterCheckboxes displays filter checkboxes for brands and benefits in the map search section.
 * Handles rendering of brand and benefit options with form integration.
 */
import { CheckboxGroup } from "@/components/atoms/checkbox-group";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IMapFilterForm, MapFilter } from "./MapFilter";
import {
  GroupOption,
  SelectOption,
} from "../../search-avaliable/SearchAvailableRoom";
import { Divider } from "@/components/atoms/divider";
import { DateRange } from "react-day-picker";

interface IMapFilterCheckboxesProps {
  currentBrand?: HotelBrandItemNullable;
  filteredBrandOptions: {
    value: string;
    label: string;
  }[];
  benefitOption?: {
    value: string;
    label: string;
  }[];
}

const MapFilterCheckboxes: React.FC<
  IMapFilterCheckboxesProps & {
    listOption: SelectOption[] | GroupOption[];
    locale: string;
    subtitleBrand?: string;
    subtitleConditions?: string;
    range: DateRange;
    setRange: React.Dispatch<React.SetStateAction<DateRange>>;
  }
> = ({
  currentBrand,
  filteredBrandOptions,
  benefitOption,
  listOption,
  locale,
  subtitleBrand,
  subtitleConditions,
  range,
  setRange,
}) => {
  const { control } = useFormContext<IMapFilterForm>();
  // Render filter UI for brands and benefits, only if not selecting a specific hotel
  return (
    <div className="max-h-[60svh] overflow-y-auto md:max-h-[68vh]">
      <div className="flex flex-col gap-4 px-3 py-6 lg:px-6">
        {/* Main map filter for hotel/city/date selection */}
        <MapFilter
          listOption={listOption}
          locale={locale}
          range={range}
          setRange={setRange}
        />
        {/* Brand filter checkboxes, only shown if no hotel is selected */}
        <div className={`transition-all duration-300`}>
          {!currentBrand && (
            <div className="pb-5">
              <Controller
                name="brands"
                control={control}
                render={({ field }) => (
                  <div className="w-full">
                    {/* Checkbox group for brand selection */}
                    <CheckboxGroup
                      className="col-span-1 mt-4"
                      labelClassName="text-wrap"
                      title={subtitleBrand || "ブランド"}
                      options={filteredBrandOptions ?? []}
                      listClassName="grid md:grid-cols-2 gap-2 md:gap-x-4 items-center w-full !space-y-0 gap-y-2"
                      selectedOptions={field.value}
                      onChange={(newSelectedBrands) =>
                        field.onChange(newSelectedBrands)
                      }
                    />
                  </div>
                )}
              />
            </div>
          )}
          <Divider />
          {/* Benefit filter checkboxes */}
          <div className="pt-5">
            <Controller
              name="preferences"
              control={control}
              render={({ field }) => (
                <div className="w-full">
                  {/* Checkbox group for benefit selection */}
                  <CheckboxGroup
                    className="col-span-1 mt-4"
                    labelClassName="text-wrap"
                    title={subtitleConditions || "条件の絞り込み"}
                    options={benefitOption ?? []}
                    listClassName="grid md:grid-cols-2 gap-2 md:gap-x-4 items-center w-full !space-y-0 gap-y-2"
                    selectedOptions={field.value}
                    onChange={(newSelectedPreferences) =>
                      field.onChange(newSelectedPreferences)
                    }
                  />
                </div>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapFilterCheckboxes;
